import { Users, MessageCircle, Search, Shield, AlertCircle, BookOpen } from 'lucide-react';
import { MainBodyData, KeyTakeawaysData } from './types';

export const humintContent: MainBodyData = {
  number: '08',
  title: 'Human Intelligence (HUMINT)',
  icon: Users,
  sections: {
    overview: "Human Intelligence (HUMINT) is information gathered through direct or indirect interaction with people rather than technical means — interviews, elicitation, forums, and other human-sourced conversation. In an OSINT context this covers publicly accessible human interactions: interviews, panel talks, forum/Discord posts, reviews, and direct (ethical) elicitation.",
    note: "Never misrepresent yourself in a way that breaks platform terms of service or the law. Elicitation must stay within legal and ethical bounds — no impersonation of officials, no deception that causes harm.",
    steps: [
      { title: "Identify Human Sources", description: "Who has first-hand knowledge? Colleagues, neighbours, forum members, event attendees." },
      { title: "Passive Collection", description: "Read interviews, podcasts, panel talks, and public Q&As before ever engaging directly." },
      { title: "Community Engagement", description: "Observe forums, Discord/Slack communities, and comment sections for candid, unscripted detail." },
      { title: "Elicitation", description: "If direct contact is appropriate, use open, non-leading questions to draw out information naturally." },
      { title: "Source Verification", description: "Corroborate claims from a human source against independent evidence before treating them as fact." },
    ],
    platforms: ["Reddit", "Discord", "Forums (niche/industry-specific)", "Podcasts", "Conference Q&As", "Glassdoor / Indeed reviews"],
    dataPoints: [
      "First-hand accounts and direct quotes",
      "Internal terminology, roles, and org structure mentioned informally",
      "Sentiment and morale signals (reviews, forum tone)",
      "Corroborating or contradicting details vs. official statements",
      "Contact patterns and who talks to whom in a community",
      "Source reliability and potential bias or motive",
    ]
  }
};

export const humintTakeaways: KeyTakeawaysData = {
  sectionTitle: "HUMINT",
  takeaways: [
    "Read and listen before engaging — passive collection from interviews and public Q&As is lower-risk than direct contact",
    "Never misrepresent identity in ways that violate platform terms or the law",
    "Treat any single human account as a lead, not a fact, until corroborated",
    "Assess source motive, access, and reliability separately for every claim",
    "Open, non-leading questions surface more than closed or suggestive ones",
    "Document exactly what was said, by whom, and when — verbatim where possible",
  ],
  tools: [
    { name: "Reddit Search", category: "COMMUNITY", description: "Search subreddits for first-hand accounts and discussion", url: "reddit.com", icon: MessageCircle },
    { name: "Discord Search Engines", category: "COMMUNITY", description: "Indexed search across public Discord servers", url: "discord.com", icon: Search },
    { name: "Glassdoor", category: "REVIEWS", description: "Employee reviews revealing internal culture and structure", url: "glassdoor.com", icon: BookOpen },
    { name: "OSINT Framework", category: "DIRECTORY", description: "Curated directory of human-source and community OSINT tools", url: "osintframework.com", icon: Shield },
    { name: "YouTube / Podcast Search", category: "INTERVIEWS", description: "Find interviews and panel talks featuring the subject", url: "youtube.com", icon: Users },
    { name: "Verification Handbook", category: "METHOD", description: "Best-practice guidance for verifying human-sourced claims", url: "verificationhandbook.com", icon: AlertCircle },
  ]
};
